import { generateClient } from "aws-amplify/api";
import { list_tickets } from "@/graphql/queries";

const client = generateClient();

const STATUS_DISPLAY = {
  OPEN: "Awaiting RCS Support",
  PENDING_SUPPORT_ACTION: "Awaiting RCS Support",
  AWAITING_RCS_SUPPORT: "Awaiting RCS Support",
  AWAITING_SUPPORT: "Awaiting RCS Support",
  PENDING_CUSTOMER_ACTION: "In progress",
  AWAITING_YOUR_REPLY: "In progress",
  AWAITING_USER_REPLY: "In progress",
  AWAITING_USER: "In progress",
  AWAITING_REPLY: "In progress",
  IN_PROGRESS: "In progress",
  RESOLVED: "Resolved",
  AUTO_CLOSED: "Auto-closed",
  AUTOCLOSED: "Auto-closed",
};

const STATUS_API = {
  "Awaiting RCS Support": "PENDING_SUPPORT_ACTION",
  "In progress": "PENDING_CUSTOMER_ACTION",
  "Awaiting your reply": "PENDING_CUSTOMER_ACTION",
  Resolved: "RESOLVED",
  "Auto-closed": "AUTO_CLOSED",
};

export function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function displayStatus(rawStatus) {
  if (!rawStatus) return "Awaiting RCS Support";
  const key = String(rawStatus).trim();
  if (STATUS_DISPLAY[key]) return STATUS_DISPLAY[key];
  const upper = key.toUpperCase().replace(/\s+/g, "_");
  if (STATUS_DISPLAY[upper]) return STATUS_DISPLAY[upper];
  if (Object.values(STATUS_DISPLAY).includes(key)) return key;
  return key;
}

export function mapAuthorType(authorType = "") {
  const type = String(authorType).toUpperCase();
  if (type === "STAFF" || type === "SUPPORT" || type === "AGENT") return "support";
  if (type === "AI" || type === "BOT" || type === "ASSISTANT") return "bot";
  return "user";
}

export function mapMessageSender(entry = {}) {
  const candidates = [
    entry.author_name,
    entry.authorName,
    entry.author_email,
    entry.authorEmail,
    entry.author_email_id,
    entry.sender,
    entry.sender_name,
    entry.senderName,
    entry.sender_email,
    entry.user_email_id,
    entry.userEmail,
    entry.email,
    entry.created_by,
    entry.createdBy,
    entry.created_by_email,
    entry.agent_email_id,
    entry.agent_name,
    entry.reporter_name,
    entry.reporterName,
    entry.reporter_email,
    entry.reporterEmail,
  ];
  const sender = candidates.find(
    (value) => value && String(value).trim() && String(value).trim().toLowerCase() !== "unknown"
  );
  return sender ? String(sender).trim() : "Unknown";
}

export function normalizeAttachmentUrls(attachments) {
  if (!attachments) return [];
  let list = attachments;
  if (typeof attachments === "string") {
    try {
      const parsed = JSON.parse(attachments);
      list = Array.isArray(parsed) ? parsed : [attachments];
    } catch {
      list = [attachments];
    }
  }
  list = Array.isArray(list) ? list : [list];
  return list
    .map((item) => {
      if (!item) return "";
      if (typeof item === "string") return item.trim();
      return String(
        item.url ||
          item.file_url ||
          item.fileUrl ||
          item.presigned_url ||
          item.presignedUrl ||
          item.path ||
          item.key ||
          ""
      ).trim();
    })
    .filter(Boolean);
}

export function mapMessage(entry = {}) {
  const attachments = normalizeAttachmentUrls(
    entry.attachments || entry.attachment_urls || entry.files || []
  );
  const fileUrl =
    entry.file_url || entry.fileUrl || entry.file_path || entry.attachment_url || "";
  if (fileUrl && !attachments.includes(fileUrl)) {
    attachments.push(fileUrl);
  }
  return {
    id:
      entry.id ||
      entry.correspondence_id ||
      entry.message_id ||
      entry.correspondenceId ||
      entry.messageId ||
      "",
    type: mapAuthorType(entry.author_type || entry.authorType || entry.type),
    sender: mapMessageSender(entry),
    time: formatDate(
      entry.created_on || entry.createdAt || entry.created_at || entry.timestamp || entry.time
    ),
    text: entry.message || entry.text || entry.content || "",
    attachments,
    raw: entry,
  };
}

export function normalizeChatbotLogs(rawLogs) {
  let logs = rawLogs;
  if (typeof logs === "string") {
    try {
      logs = JSON.parse(logs);
    } catch {
      logs = [];
    }
  }
  return Array.isArray(logs) ? logs : [];
}

export function mapTicket(raw = {}, correspondence = null) {
  const ticketId =
    raw.ticket_id || raw.ticketId || raw.id || raw.caseId || raw.case_id || "";
  const title = raw.title || raw.subject || "Untitled case";
  const moduleName = raw.module || raw.moduleName || "";
  const reporter =
    raw.reporter_name ||
    raw.reporterName ||
    raw.reporter_email ||
    raw.reporterEmail ||
    raw.reporter_user_id ||
    raw.reporterUserId ||
    "";

  const msgs = correspondence ||
    raw.correspondence ||
    raw.messages ||
    raw.thread ||
    raw.conversation ||
    [];

  const statusRaw = raw.ticket_status || raw.status || raw.ticketStatus || "";
  const status = displayStatus(statusRaw);

  return {
    ...raw,
    ticketId,
    caseId: ticketId,
    fullCaseId: ticketId,
    subject: title,
    description: raw.description || "",
    meta: [moduleName ? `${moduleName} module` : null, reporter]
      .filter(Boolean)
      .join(" · "),
    status,
    statusRaw: String(statusRaw || "").toUpperCase(),
    statusKey: String(statusRaw || status)
      .toLowerCase()
      .replace(/\s+/g, "_"),
    severity: raw.severity || raw.priority || "Normal",
    ticketType: raw.ticket_type || "",
    module: moduleName || "-",
    reporter: reporter || "-",
    source: raw.source || "AI_ESCALATION",
    facility: raw.facility_id || raw.facilityName || raw.facility || "-",
    assignedTo:
      raw.assigned_to_name ||
      raw.assignedToName ||
      raw.assigned_to_agent_id ||
      "-",
    created: formatDate(raw.created_on || raw.createdAt || raw.created_at || raw.created),
    updated: formatDate(
      raw.updated_on || raw.last_activity_on || raw.updatedAt || raw.updated_at || raw.updated
    ),
    messages: Array.isArray(msgs) ? msgs.map(mapMessage) : [],
    chatbotLogs: normalizeChatbotLogs(raw.chatbot_logs || raw.chatbotLogs),
    aiOriginSummary: raw.ai_origin_summary || raw.aiOriginSummary || "",
    resolutionSummary: raw.resolution_summary || raw.resolutionSummary || "",
  };
}

export default {
  methods: {
    toApiStatus(display) {
      return STATUS_API[display] || display;
    },
    async apiListTickets(filters = {}) {
      const input = {};
      if (filters.organizationId) input.organization_id = filters.organizationId;
      if (filters.status && filters.status !== "All") {
        input.ticket_status = this.toApiStatus(filters.status);
      }
      if (filters.limit) input.limit = filters.limit;

      const response = await client.graphql({
        query: list_tickets,
        variables: Object.keys(input).length ? { input } : undefined,
      });
      const rawPayload = response.data.list_tickets;
      const responseData =
        typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
      if (responseData.status == "SUCCESS") {
        const data = responseData.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data?.tickets)
              ? data.tickets
              : [];
        return list.map((item) => mapTicket(item));
      }
      return [];
    },
  },
};
